import { useState, useEffect, useCallback } from 'react'
import { FiSlash, FiLoader, FiUsers, FiSearch, FiCheckCircle, FiAlertTriangle, FiX } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'

/**
 * Ações sobre a LÍDER (não sobre um lead), dentro da seção LEADS ENCAMINHADOS
 * do perfil do contato — quando o contato aberto na conversa É uma líder.
 *
 * Pedido de 17/07/2026: quem descobre que a líder pediu baixa está lendo a
 * conversa com ela, não navegando no painel Bloqueados. As duas ações andam
 * juntas na prática — bloquear resolve o lead novo, transferir resolve os que
 * ela já tinha em mãos (que senão ficam órfãos, sem ninguém tratando).
 *
 * Admin-only (as duas rotas são adminMiddleware) — mesmo precedente da seção
 * BLOQUEIO de contato logo abaixo: botão visível pra não-admin só daria 403.
 * O "Trocar líder" por-lead segue disponível pro operador comum.
 */

interface LeaderBlockRow {
  id: number
  codigo: string
  reason: string
  blocked_by: string
  active: number
}

interface LeaderOption {
  id: number | string
  code: string
  name: string
  grupo_nome?: string | null
  endereco_cidade?: string | null
}

interface TransferReport {
  total: number
  dest_name: string
  transferred: { leadName: string }[]
  failed: { leadName: string; error: string }[]
}

interface Props {
  code: string
  leaderName: string
  pending: number
  onChanged: () => void
  neu?: boolean
}

export function LeaderActionsBar({ code, leaderName, pending, onChanged, neu = false }: Props) {
  const { authenticatedFetch, user } = useAuth()
  const [block, setBlock] = useState<LeaderBlockRow | null>(null)
  const [mode, setMode] = useState<'none' | 'block' | 'transfer'>('none')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [leaders, setLeaders] = useState<LeaderOption[] | null>(null)
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<LeaderOption | null>(null)
  const [report, setReport] = useState<TransferReport | null>(null)

  const digits = (s: string) => String(s || '').replace(/\D/g, '')

  const loadBlock = useCallback(async () => {
    if (!user?.isAdmin) return
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.LEADER_BLOCKLIST)
      const d = await r.json()
      if (!r.ok || !d.success) return
      const hit = (d.entries || []).find(
        (e: LeaderBlockRow) => e.active === 1 && digits(e.codigo) === digits(code)
      )
      setBlock(hit || null)
    } catch { /* estado do bloqueio é informativo — erro não quebra a seção */ }
  }, [authenticatedFetch, code, user?.isAdmin])

  useEffect(() => { loadBlock() }, [loadBlock])

  // /api/leaders já exclui as bloqueadas (passa pelo buildLeadersQuery), então a
  // lista de destino nunca oferece quem não pode receber.
  const loadLeaders = useCallback(async () => {
    if (leaders) return
    try {
      const r = await authenticatedFetch('/api/leaders')
      const d = await r.json()
      setLeaders(Array.isArray(d) ? d : (d.leaders || []))
    } catch {
      setError('Não foi possível carregar a lista de líderes.')
      setLeaders([])
    }
  }, [authenticatedFetch, leaders])

  const doBlock = async () => {
    if (!reason.trim()) return
    setBusy(true); setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.LEADER_BLOCKLIST, {
        method: 'POST',
        body: JSON.stringify({ codigo: code, reason }),
      })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      setReason(''); setMode('none')
      await loadBlock()
      onChanged()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const doTransfer = async () => {
    if (!picked) return
    setBusy(true); setError(null); setReport(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.LEADER_TRANSFER_OPEN_LEADS(code), {
        method: 'POST',
        body: JSON.stringify({ toLeaderId: picked.id }),
      })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`)
      setReport(d)
      setPicked(null); setQuery('')
      onChanged()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (!user?.isAdmin) return null

  const hits = (leaders || [])
    .filter(l => digits(l.code) !== digits(code))
    .filter(l => {
      const q = query.trim().toLowerCase()
      if (!q) return false
      return l.name?.toLowerCase().includes(q) || String(l.code).includes(q)
    })
    .slice(0, 8)

  return (
    <div className={`px-2.5 py-2 space-y-2 ${neu ? 'neu-card' : 'rounded-md border border-border/70 bg-muted/20'}`}>
      {block ? (
        <div className="flex items-start gap-1.5">
          <FiSlash className="w-3 h-3 text-red-500 shrink-0 mt-0.5" title="Líder bloqueada" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold text-red-600 dark:text-red-400">Líder bloqueada</div>
            <div className="text-[10px] text-muted-foreground leading-relaxed" title={block.reason}>
              Não recebe leads novos. Motivo: {block.reason}
            </div>
            <div className="text-[9px] text-muted-foreground/70 mt-0.5">
              Para desbloquear, use Recrutamento → Bloqueados → Líderes.
            </div>
          </div>
        </div>
      ) : (
        <div className="text-[10px] text-muted-foreground leading-relaxed">
          Ações sobre a líder — bloquear tira ela da fila de novos leads; transferir passa os
          {' '}{pending} pendente{pending === 1 ? '' : 's'} pra outra líder.
        </div>
      )}

      {error && (
        <div className="text-[10px] text-destructive flex items-start gap-1.5">
          <FiAlertTriangle className="w-3 h-3 shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {report && (
        <div className="rounded border border-border bg-card px-2 py-1.5 space-y-1">
          <div className="text-[10px] font-semibold text-foreground flex items-center gap-1.5">
            <FiCheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />
            {report.transferred.length} de {report.total} transferido{report.transferred.length === 1 ? '' : 's'} para {report.dest_name}
          </div>
          {report.failed.length > 0 && (
            <div className="text-[10px] text-amber-600 dark:text-amber-400">
              <div className="flex items-center gap-1.5 font-medium">
                <FiAlertTriangle className="w-3 h-3 shrink-0" /> {report.failed.length} não passou:
              </div>
              <ul className="pl-4 list-disc">
                {report.failed.map((f, i) => (
                  <li key={i} title={f.error}>{f.leadName} — {f.error}</li>
                ))}
              </ul>
            </div>
          )}
          <button
            onClick={() => setReport(null)}
            className="text-[9px] text-muted-foreground hover:text-foreground"
            title="Fechar este resumo"
          >
            fechar
          </button>
        </div>
      )}

      {mode === 'none' && (
        <div className="flex items-center gap-1.5">
          {!block && (
            <button
              onClick={() => { setMode('block'); setError(null) }}
              title={`Bloquear ${leaderName} de receber novos leads`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10"
            >
              <FiSlash className="w-3 h-3" /> Bloquear líder
            </button>
          )}
          {pending > 0 && (
            <button
              onClick={() => { setMode('transfer'); setError(null); loadLeaders() }}
              title={`Transferir os ${pending} leads pendentes desta líder para outra`}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium ${neu ? 'neu-convex text-foreground' : 'border border-border text-foreground hover:bg-secondary'}`}
            >
              <FiUsers className="w-3 h-3" /> Transferir {pending} lead{pending === 1 ? '' : 's'}
            </button>
          )}
        </div>
      )}

      {mode === 'block' && (
        <div className="space-y-1.5">
          <input
            value={reason}
            onChange={e => setReason(e.target.value)}
            maxLength={255}
            autoFocus
            placeholder="Motivo (obrigatório) — ex: pediu baixa, aguardando DRACO"
            title="O motivo fica registrado para auditoria"
            className="w-full px-2 py-1 rounded bg-background border border-border text-[11px] text-foreground placeholder:text-muted-foreground"
          />
          <div className="text-[9px] text-muted-foreground leading-relaxed">
            Não mexe nos leads que ela já tem — para esses, use Transferir.
          </div>
          <div className="flex items-center justify-end gap-1.5">
            <button
              onClick={() => { setMode('none'); setReason(''); setError(null) }}
              className="px-2 py-1 rounded text-[10px] text-muted-foreground hover:text-foreground"
            >
              Cancelar
            </button>
            <button
              onClick={doBlock}
              disabled={busy || !reason.trim()}
              title={!reason.trim() ? 'O motivo é obrigatório' : `Bloquear ${leaderName}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
            >
              {busy ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiSlash className="w-3 h-3" />}
              {busy ? 'Bloqueando…' : 'Bloquear'}
            </button>
          </div>
        </div>
      )}

      {mode === 'transfer' && (
        <div className="space-y-1.5">
          {picked ? (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-background border border-border">
              <FiCheckCircle className="w-3 h-3 text-emerald-500 shrink-0" title="Líder de destino" />
              <span className="text-[11px] text-foreground truncate flex-1">{picked.name}</span>
              <button onClick={() => setPicked(null)} title="Escolher outra líder" className="text-muted-foreground hover:text-foreground">
                <FiX className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <FiSearch className="w-3 h-3 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                autoFocus
                placeholder={leaders === null ? 'Carregando líderes…' : 'Buscar líder de destino…'}
                title="Digite o nome ou código da líder que vai receber os leads"
                className="w-full pl-7 pr-2 py-1 rounded bg-background border border-border text-[11px] text-foreground placeholder:text-muted-foreground"
              />
              {hits.length > 0 && (
                <ul className="absolute z-20 left-0 right-0 mt-1 max-h-40 overflow-y-auto rounded border border-border bg-background shadow-lg divide-y divide-border/40">
                  {hits.map(l => (
                    <li key={l.code}>
                      <button
                        onClick={() => { setPicked(l); setQuery('') }}
                        title={`Transferir os leads para ${l.name}`}
                        className="w-full text-left px-2 py-1.5 hover:bg-muted/60"
                      >
                        <div className="text-[11px] text-foreground truncate">{l.name}</div>
                        <div className="text-[9px] text-muted-foreground truncate">
                          #{l.code}{l.endereco_cidade ? ` · ${l.endereco_cidade}` : ''}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="text-[9px] text-muted-foreground leading-relaxed">
            Os {pending} leads pendentes vão para a líder escolhida, e ela recebe um WhatsApp por lead
            com os dados de cada um. A lead também é avisada da troca. Não dá pra desfazer.
          </div>
          <div className="flex items-center justify-end gap-1.5">
            <button
              onClick={() => { setMode('none'); setPicked(null); setQuery(''); setError(null) }}
              className="px-2 py-1 rounded text-[10px] text-muted-foreground hover:text-foreground"
            >
              Cancelar
            </button>
            <button
              onClick={doTransfer}
              disabled={busy || !picked}
              title={!picked ? 'Escolha a líder de destino' : `Transferir ${pending} leads para ${picked.name}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-60"
            >
              {busy ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiUsers className="w-3 h-3" />}
              {busy ? 'Transferindo…' : `Transferir ${pending}`}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
